import type { ThemeAIRun, ThemeAITask } from '../types/gotypes'

// Test is not a step of its own any more: Run asks whether to test first
// (Adam, 23 Sep: "maybe you get rid of this whole test step, and you just
// have run. But then as part of run, there's a, do you want to test this
// before you do it?"). The test is a view inside Run — see
// resolveThemeAIStageParam.
export type ThemeAITaskStage = 'data' | 'code_frame' | 'run' | 'results'

/** A view inside a stage. Only Run has them: the sample test, the junk review and the personal data review. */
export type ThemeAIRunView = 'run' | 'test' | 'junk' | 'personal-data'

export const THEME_AI_STAGE_ORDER: { key: ThemeAITaskStage; label: string }[] = [
  { key: 'data', label: 'Data' },
  { key: 'code_frame', label: 'Code Frame' },
  { key: 'run', label: 'Run' },
  { key: 'results', label: 'Results' },
]

export type ThemeAIStageStatus = 'done' | 'current' | 'available' | 'locked'

export interface ThemeAIStageState {
  status: ThemeAIStageStatus
  /** Short reason shown by the stepper when status === 'locked'. */
  lockedReason?: string
}

export interface ThemeAIStageProgress {
  completed: ThemeAITaskStage[]
  reachable: ThemeAITaskStage[]
  /** The stage the page should default to / navigate to next. */
  next: ThemeAITaskStage
  /** Per-stage status for the stepper, keyed by stage. */
  stages: Record<ThemeAITaskStage, ThemeAIStageState>
}

const LOCKED_REASONS: Record<ThemeAITaskStage, string> = {
  data: '',
  code_frame: 'Needs data',
  run: 'Needs a code frame',
  results: 'Needs a run',
}

export const isThemeAITaskStage = (value: unknown): value is ThemeAITaskStage =>
  typeof value === 'string' && THEME_AI_STAGE_ORDER.some((stage) => stage.key === value)

/**
 * Reads the `stage` route param. `?stage=test` still works: it opens Run with
 * its test view, so links made when Test was a step land in the same place.
 * `?stage=junk` opens Run with the junk review (#1891), `?stage=personal-data`
 * with the personal data review (#494).
 */
export const resolveThemeAIStageParam = (
  value: unknown,
): { stage: ThemeAITaskStage; view?: Exclude<ThemeAIRunView, 'run'> } | undefined => {
  if (value === 'test') return { stage: 'run', view: 'test' }
  if (value === 'junk') return { stage: 'run', view: 'junk' }
  if (value === 'personal-data') return { stage: 'run', view: 'personal-data' }
  return isThemeAITaskStage(value) ? { stage: value } : undefined
}

export const hasReadyThemeAIData = (task: ThemeAITask | undefined): boolean => {
  const source = task?.data_source
  if (source?.type === 'job_variable') return Boolean(source.job_variable?.variable_name)
  if (source?.type === 'upload') {
    return Boolean(source.upload?.blob_path && (source.upload.row_count || 0) > 0)
  }
  return false
}

// "Current run" (API contract, themeai-ui-pass): the server-owned
// data_revision counter plus the pinned code frame must all match the task's
// live values. Replaces the labels["themeai_data_revision_ms"] heuristic
// (PR #1483) now that the backend stamps a real revision on both sides.
export const isThemeAIRunCurrent = (
  task: ThemeAITask | undefined,
  run: ThemeAIRun,
): boolean => Boolean(
  task
  && run.data_revision === task.data_revision
  && run.code_frame_id === task.code_frame_id
  && run.code_frame_version === task.code_frame_version,
)

export const deriveThemeAIStageProgress = (
  task: ThemeAITask | undefined,
  runs: ThemeAIRun[] | undefined,
): ThemeAIStageProgress => {
  const dataReady = hasReadyThemeAIData(task)
  const frameReady = Boolean(task?.code_frame_id && task.code_frame_version)

  const allRuns = runs || []
  // Source replacement keeps old runs as audit evidence (they stay reachable
  // from Results), but only CURRENT runs can unlock stages for the live pool.
  const currentRuns = allRuns.filter((run) => isThemeAIRunCurrent(task, run))
  // A test run never moves the task on: it is optional and lives inside Run.
  const currentProductionRuns = currentRuns.filter((run) => !run.is_test)
  const productionComplete = currentProductionRuns.some((run) => run.status === 'completed')

  // Strict prefix chain: each stage's reachability requires every stage
  // before it. Results is the one deliberate exception — any run at all
  // (current or historical, test or production) is evidence worth reviewing.
  const reachable: ThemeAITaskStage[] = [
    'data',
    ...(dataReady ? ['code_frame' as const] : []),
    // A test is recommended, not required (Matt, 21 Sep: "50, 100, 200 or
    // no test"), so Run opens once there is a frame.
    ...(dataReady && frameReady ? ['run' as const] : []),
    ...(allRuns.length > 0 ? ['results' as const] : []),
  ]

  const completed: ThemeAITaskStage[] = [
    ...(dataReady ? ['data' as const] : []),
    ...(dataReady && frameReady ? ['code_frame' as const] : []),
    ...(dataReady && frameReady && productionComplete ? ['run' as const] : []),
    // A finished run has results to read, so the last step is done too: a
    // coded task shows four ticks (Adam, 24 Sep).
    ...(dataReady && frameReady && productionComplete ? ['results' as const] : []),
  ]

  // Once a current production run exists at all, the useful place to land is
  // Results (to watch it live / see why it failed), not back on Run — mirrors
  // the pre-existing behaviour, now driven by the current-run definition.
  const next: ThemeAITaskStage = !dataReady
    ? 'data'
    : !frameReady
      ? 'code_frame'
      : currentProductionRuns.length === 0
        ? 'run'
        : 'results'

  const stages = THEME_AI_STAGE_ORDER.reduce((acc, { key }) => {
    if (completed.includes(key)) {
      acc[key] = { status: 'done' }
    } else if (!reachable.includes(key)) {
      acc[key] = { status: 'locked', lockedReason: LOCKED_REASONS[key] }
    } else if (key === next) {
      acc[key] = { status: 'current' }
    } else {
      acc[key] = { status: 'available' }
    }
    return acc
  }, {} as Record<ThemeAITaskStage, ThemeAIStageState>)

  return { completed, reachable, next, stages }
}
